import {
	ALERT_HIDDEN,
	EMAILS_TAB,
	NOTES_TAB,
	SIGNATURE_OPTION,
	SECURE_FILE_TAB,
	TEXTO_TAB
} from "@/common/const";
import { getField, updateField } from "vuex-map-fields";
import { insertSorted, compareConversations, sortConversationSids, getSortedPosition } from "@/common/utils";
import { fetchMessages } from "@/services/conversationService";
import { Mutex } from "../common/mutex";
import axios from "axios";
import { bus } from "@/main.js";
import moment from "moment";
import {
	getConversation,
	setActiveComposable,
	ensureConversation,
	updateConversation as updateConvo
} from "@/composables/conversation";

const mutex = new Mutex();

const conversationModule = {
	init({ watch, login, state, commit }, rootStore) {
		let conversationsHook = null;
		let versionReady = false;
		let bootstrapped = false;
		const lifecycleMutex = new Mutex();

		watch(
			() => [login.userGroup?.id, login.profile?.clinic, state.messagingVersion, Boolean(login.jwt)],
			(current, previous) => {
				if (current.some((value, index) => value !== previous[index])) {
					commit("invalidateSelection");
				}
			},
			{ sync: true }
		);

		// True when the app started with a persisted session (page refresh): the
		// session restore (login/onRefresh → signInEvents) is then in flight and
		// bootstrap must wait for login.refreshed. Fresh logins never set
		// refreshed, so they must not be gated on it.
		let awaitingSessionRestore = Boolean(login.jwt);

		const bootstrap = async () => {
			await lifecycleMutex.lock();

			try {
				if (bootstrapped || !versionReady || !login.userGroup?.id
					|| (awaitingSessionRestore && !login.refreshed)) return;

				bootstrapped = true;
				const version = state.messagingVersion;
				const userGroupId = login.userGroup.id;
				const { default: useConversation } = version >= 2
					? await import("@/composables/conversation-v2")
					: await import("@/composables/conversation");

				if (version !== state.messagingVersion || userGroupId !== login.userGroup?.id) {
					bootstrapped = false;
					return;
				}

				conversationsHook = useConversation();
				setActiveComposable(conversationsHook);
				await conversationsHook.bootstrap();
			} catch (error) {
				bootstrapped = false;
				console.error("[CONVERSATION] Failed to bootstrap conversations:", error);
			} finally {
				lifecycleMutex.unlock();
			}
		};

		const cleanup = async () => {
			commit("invalidateSelection");

			await lifecycleMutex.lock();

			try {
				await conversationsHook?.cleanup(commit);
				conversationsHook = null;
				bootstrapped = false;
				setActiveComposable(null);
			} finally {
				lifecycleMutex.unlock();
			}
		};

		// Wait for clinic data to load before allowing bootstrap
		rootStore.watch(
			(state) => state.clinic?.clinicData,
			async (val) => {
				if (val?.id) {
					const version = val.messagingApiVersion ?? 1;
					const versionChanged = versionReady && version !== state.messagingVersion;

					commit("setVersion", version);

					if (versionChanged) {
						await cleanup();
					}

					if (!versionReady || versionChanged) {
						versionReady = true;

						// If userGroup is already set, bootstrap now — but on a page
						// refresh only once the session restore has completed:
						// login/onRefresh re-signs the events auth (signInEvents), which
						// races with the Firestore subscription bootstrap creates and can
						// leave the app hanging on the first snapshot. When the restore is
						// still pending, the login.refreshed watcher below bootstraps.
						if (login.userGroup?.id && !conversationsHook
							&& (!awaitingSessionRestore || login.refreshed)) {
							await bootstrap();
						}
					}
				}
			},
			{ immediate: true }
		);

		// Page refresh: bootstrap once the session restore (login/onRefresh →
		// signInEvents) has completed. Fresh logins are handled by the
		// userGroup.id watcher instead (refreshed stays false in that flow).
		watch(
			() => login.refreshed,
			(refreshed) => {
				if (refreshed) {
					awaitingSessionRestore = false;
				}

				if (refreshed && versionReady && login.userGroup?.id && !bootstrapped) {
					bootstrap();
				}
			}
		);

		watch(
			() => login.userGroup?.id,
			async (newId, oldId) => {
				if (newId === oldId) return;

				if (versionReady) {
					console.debug(`[CONVERSATION-V${state.messagingVersion}] userGroupId changed:`, oldId, "->", newId);
				}

				if (oldId) {
					await cleanup();
				}

				if (!newId && oldId) {
					awaitingSessionRestore = false;
					setActiveComposable(null);
				} else if (newId && versionReady) {
					bootstrap();
				}
			}
		);
	},
	getters: {
		version: (state) => state.messagingVersion,
		selectionGeneration: state => state.selectionGeneration,
		loadingAllConversations: state => {
			return state.loadingAllConversations;
		},

		loadingConversation: state => {
			return state.loadingConversation;
		},

		userGroupId: state => {
			return state.userGroupId;
		},

		loaded: state => {
			return state.loaded;
		},

		activePatient: state => {
			return state.patient;
		},

		patient: (state) => {
			return state.patient;
		},

		phoneNumber: (state) => {
			return state.singleConversation?.friendlyName ??
					state.singleConversation?.channelState?.friendlyName;
		},

		hasConversation: state => {
			return state.actives.length > 0 || state.inactives.length > 0;
		},

		surveyArray: state => {
			return state.surveyArray;
		},

		singleConversation: state => {
			return state.singleConversation;
		},

		actives : state => {
			return state.actives;
		},

		inactives : state => {
			return state.inactives;
		},

		pinned: state => {
			return state.pinned;
		},

		prepareToCallLoader: state => {
			return state.prepareToCallLoader;
		},

		unreadConversationCount: state => {
			return state.unreadConversationCount;
		},

		unreadInactiveConversationCount: state => {
			return state.unreadInactiveConversationCount;
		},

		// V2 cursor pagination: whether a next / previous page exists for a state.
		pageHasNext: state => type =>
			(type === "active" ? state.hasNextActive : state.hasNextInactive),

		pageHasPrev: state => type =>
			(type === "active" ? state.hasPrevActive : state.hasPrevInactive),

		messages: state => {
			return state.messages;
		},

		isInitializing: state => {
			// Only the initial connection/bootstrap should show the full-screen loader.
			// `loadingAllConversations` also flips true when switching between active/inactive
			// lists; including it here would unmount ConversationsList on every toggle and
			// reset its local state (e.g. filterArchive), hiding the archived list.
			return state.isInitializing;
		},

		connectionState: state => {
			return state.connectionState;
		},

		hasUnreadSurveys: state => {
			return !!state.singleConversation?.channelState.attributes?.hasUnreadSurveys
				? state.hasUnreadSurveys && !!state.singleConversation?.channelState.attributes?.hasUnreadSurveys
				: !!state.singleConversation?.channelState?.attributes?.hasUnreadSurveys;
		},

		isInboundMessage: () => message => {
			if (message?.direction) {
				return message.direction === "inbound";
			}

			return !!message?.author && /^\+\d+/i.test(message.author); // V1
		},

		hasNotifications: () => conversation => {
			// V2 Conversation class maps unread → channelState.attributes.hasNotification
			return !!conversation?.unread || !!conversation?.channelState?.attributes?.hasNotification;
		},

		hasPendingNotifications: state => {
			return !!state.unreadConversationCount || !!state.unreadInactiveConversationCount;
		},

		hasDisplayedConversation: state => {
			return !!state.singleConversation?.sid;
		},

		loadingMessagesError: state => {
			return state.loadingMessagesError;
		},

		loadingMessages: state => {
			return state.loadingMessages;
		},

		selectedAppointmentMessage: state => {
			return state.selectedAppointmentMessage;
		},

		contactAppointments: state => {
			return state.contactAppointments;
		},

		contactOldAppointments: state => {
			return state.contactOldAppointments;
		},

		contactPendingAppointments: state => {
			return state.contactPendingAppointments;
		},

		isTextoTab: state => {
			return state.activeSendBoxTab === TEXTO_TAB;
		},

		isEmailsTab: state => {
			return state.activeSendBoxTab === EMAILS_TAB;
		},

		isSecureFileTab: state => {
			return state.activeSendBoxTab === SECURE_FILE_TAB;
		},

		isNotesTab: state => {
			return state.activeSendBoxTab === NOTES_TAB;
		},

		signatureOption: state => {
			return state.signatureOption;
		},

		focusSendMessageInput: state => {
			return state.focusSendMessageInput;
		},

		getField
	},
	mutations: {
		updateField,

		setVersion(state, version) {
			state.messagingVersion = version;
		},

		setSingleConversation(state, conversation) {
			state.singleConversation = conversation;
		},

		setFocusSendMessageInput(state, focus) {
			state.focusSendMessageInput = focus;
		},

		setLoadingAllConversations (state, loading) {
			state.loadingAllConversations = loading;
		},

		setLoadingPatient(state, loading) {
			state.loadingPatient = loading;
		},

		setPatient(state, patient) {
			state.patient = patient;
		},

		setLoadingConversation(state, loading) {
			state.loadingConversation = loading;
		},

		setLoadingMessages(state, loading) {
			state.loadingMessages = loading;
		},

		setLoadingMessagesError(state, error) {
			state.loadingMessagesError = error;
		},

		setMessages(state, messages) {
			state.messages = messages;
		},

		setMessagePagination(state, pagination) {
			state.messagePagination = pagination;
		},

		appendMessages(state, messages) {
			state.messages.push(...messages);
		},
		
		removeMessage(state, messageSid) {
			state.messages = state.messages.filter(message => message.sid !== messageSid);
		},

		setTwilioMessagePage(state, messagePage) {
			state.twilioMessagePage = messagePage;
		},

		setUserGroupId(state, userGroupId) {
			state.userGroupId = userGroupId;
		},

		invalidateSelection(state) {
			state.selectionGeneration++;
			state.loadingConversation = false;
		},

		release(state) {
			conversationModule.helpers.resetSelectedConversation(state);
			conversationModule.mutations.invalidateSelection(state);

			state.loaded = false;
			state.userGroupId = null;
			state.actives = [];
			state.inactives = [];
			state.addedConversations = {};
			state.hasNotificationSids.clear();
			state.hasNotificationInactiveSids.clear();
			state.unreadConversationCount = 0;
			state.unreadInactiveConversationCount = 0;
			state.hasNextActive = false;
			state.hasPrevActive = false;
			state.hasNextInactive = false;
			state.hasPrevInactive = false;
		},

		setAll(state, { actives, inactives, notifications, notificationInactive, addedConversations }) {
			state.actives = actives;
			state.addedConversations = addedConversations;
			state.inactives = inactives;

			if (notifications) {
				state.hasNotificationSids.clear();
				state.hasNotificationSids = notifications;
				state.unreadConversationCount = notifications.size;
			}

			if (notificationInactive) {
				state.hasNotificationInactiveSids.clear();
				state.hasNotificationInactiveSids = notificationInactive;
				state.unreadInactiveConversationCount = notificationInactive.size;
			}

			state.loaded = true;
		},

		sort(state, type) {
			const array = type === "active" ? state.actives : state.inactives;

			sortConversationSids(array);
		},

		// ---- V2 numbered pagination (backend-driven) ----
		// `actives`/`inactives` hold only the current page of SIDs. The backend
		// returns them already sorted by pinned desc, updatedAt desc, then id desc;
		// realtime reordering operates
		// on the small current-page array via `pageUpsertSorted` (O(log n)
		// binary-search insertion — no full sort).

		pageUpsertSorted(state, { type, sid, limit, insertIfAbsent = false }) {
			const array = type === "active" ? state.actives : state.inactives;
			const idx = array.indexOf(sid);

			if (idx === -1 && !insertIfAbsent) return;

			if (idx > -1) array.splice(idx, 1);

			// Mirror the backend ordering (pinned desc, updatedAt desc, then id desc)
			// so page boundaries stay consistent with the REST pages.
			const pos = getSortedPosition(array, getConversation(sid));

			array.splice(pos, 0, sid);

			// Trim after the sorted insert so the correct item falls off the page.
			if (typeof limit === "number" && limit > 0 && array.length > limit) {
				array.splice(limit);
			}
		},

		setPage(state, { type, sids, hasNext = false, hasPrev = false }) {
			if (type === "active") {
				state.actives = sids;
				state.hasNextActive = hasNext;
				state.hasPrevActive = hasPrev;
			} else {
				state.inactives = sids;
				state.hasNextInactive = hasNext;
				state.hasPrevInactive = hasPrev;
			}

			state.loaded = true;
		},

		// V2: probing past the realtime window found nothing — retract the arrow.
		setPageHasNext(state, { type, hasNext }) {
			if (type === "active") {
				state.hasNextActive = hasNext;
			} else {
				state.hasNextInactive = hasNext;
			}
		},

		// V2: the unread badge uses a separate capped realtime query, rendered
		// as "50+" at the cap, independently of the displayed list.
		setUnreadCount(state, { type, count }) {
			const value = Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;

			if (type === "active") {
				state.unreadConversationCount = value;
			} else {
				state.unreadInactiveConversationCount = value;
			}
		},

		pageRemoveSid(state, { type, sid }) {
			const array = type === "active" ? state.actives : state.inactives;
			const idx = array.indexOf(sid);

			if (idx > -1) array.splice(idx, 1);
		},

		addSingleMessage(state, message) {
			const conversationSid =
				message?.conversationSid ?? message?.conversation?.sid;

			if (conversationSid !== state.singleConversation?.sid) {
				return;
			}

			// Prevent duplicate messages from being added
			const messageSid = message.sid ?? message.state?.sid;
			const exists = state.messages.some(m => {
				const existingSid = m.sid ?? m.state?.sid;

				return existingSid === messageSid;
			});

			if (exists) {
				return;
			}

			state.messages.push(message);
		},

		addNewMessage(state, message) {
			const conversationSid =
				message?.conversationSid ?? message?.conversation?.sid;

			if (conversationSid !== state.singleConversation?.sid) {
				return;
			}

			// A deletion shifts the realtime window and re-emits the backfilled
			// older doc as "added"; skip it when pagination already loaded it.
			const messageSid = message.sid ?? message.state?.sid;
			const exists = state.messages.some(m =>
				(message.id && m.id === message.id) ||
				(messageSid && (m.sid ?? m.state?.sid) === messageSid)
			);

			if (exists) {
				return;
			}

			// Backfilled docs are older than the tail: insert chronologically.
			let pos = state.messages.length;

			while (pos > 0 && (state.messages[pos - 1].index ?? 0) > (message.index ?? Infinity)) {
				pos--;
			}

			const isNewest = pos === state.messages.length;

			state.messages = [...state.messages.slice(0, pos), message, ...state.messages.slice(pos)];

			if (isNewest) {
				state.newMessage = true;
			}
		},

		updateSingleMessage(state, message) {
			// Guard: Only update message if it belongs to the currently displayed conversation
			if (message?.conversationSid && message.conversationSid !== state.singleConversation?.sid) {
				return;
			}

			const index = state.messages.findIndex(m => (m.sid && m.sid === message.sid) || (m.id && m.id === message.id));

			if (index !== -1) {
				const existing = state.messages[index];

				Object.keys(message).forEach((key) => {
					existing[key] = message[key];
				});
			}
		},

		setSurveys(state, surveys) {
			state.surveyArray = surveys;
		},

		setNewMessage(state, isNew) {
			state.newMessage = isNew;
		},

		resetSelectedConversation(state) {
			conversationModule.helpers.resetSelectedConversation(state);
		},

		setIsInitializing(state, isInitializing) {
			state.isInitializing = isInitializing;
		},

		setConnectionState(state, connectionState) {
			if (connectionState !== state.connectionState) {
				state.connectionState = connectionState;
			}
		},

		setSelectedAppointmentMessage(state, selectedAppointmentMessage) {
			state.selectedAppointmentMessage = selectedAppointmentMessage;
		},

		setContactAppointments(state, allAppointments) {
			state.contactAppointments = allAppointments;
		},

		setOldContactAppointments(state, oldAppointments) {
			state.contactOldAppointments = oldAppointments;
		},

		setPendingContactAppointments(state, pendingAppointments) {
			state.contactPendingAppointments = pendingAppointments;
		},

		setSignatureOption(state, option) {
			state.signatureOption = option;
		},

		setAudioNotificationsEnabled(state, value) {
			state.audioNotificationsEnabled = value;
		},

		removeFromNotifications(state, sid) {
			if (state.hasNotificationSids.has(sid)) {
				state.hasNotificationSids.delete(sid);
				state.unreadConversationCount = state.hasNotificationSids.size;
			}

			if (state.hasNotificationInactiveSids.has(sid)) {
				state.hasNotificationInactiveSids.delete(sid);
				state.unreadInactiveConversationCount = state.hasNotificationInactiveSids.size;
			}
		}
	},

	helpers: {
		resetSelectedConversation(state) {
			state.singleConversation = null;
			state.patient = null;
			state.twilioMessagePage = undefined;
			state.messagePagination = { hasMore: false, nextCursor: null };
			state.messages = [];
		}
	},

	actions: {
		async loadMessages({ state, commit }, { conversation, sid }) {
			commit("setLoadingMessagesError", false);
			commit("setLoadingMessages", true);
			
			try {
				const messages = await conversation.getMessages(10);

				// Guard: Check if we're still viewing the same conversation after async call
				// This prevents stale results from overwriting a newer conversation's messages
				if (state.singleConversation?.sid !== sid) {
					console.debug("Conversation ", state.singleConversation?.sid, " no longer match after loading messages. Skipping set.", sid);
					throw new Error("Conversation mismatch after loading messages");
				}

				commit("setTwilioMessagePage", messages);

				// Filter out flow messages
				const filteredMessages = messages.items.filter(msg => !msg.attributes?.isFlowMessage);

				if (state.messages.length === 0) {
					// No existing messages, set all filtered messages
					commit("setMessages", filteredMessages);
				} else {
					// Use the last message's index directly (messages are chronologically ordered)
					const highIdx = state.messages[state.messages.length - 1]?.index ?? -1;

					// Only add messages with higher indices (newer messages)
					const newMessages = filteredMessages.filter(msg =>
						(msg.index ?? -1) > highIdx
					);

					if (newMessages.length > 0) {
						commit("appendMessages", newMessages);
					}
				}
			} catch (err) {
				console.error(`Error loading messages for conversation ${conversation.sid}: ${err}`);
				commit("setLoadingMessagesError", true);
				throw err;
			} finally {
				commit("setLoadingMessages", false);
			}
		},

		retryLoadingMessages({ state, dispatch }) {
			if (state.singleConversation?.sid) {
				dispatch("selectConversation", state.singleConversation?.sid);
			}
		},

		async selectConversation({ state, commit, getters, dispatch }, sid) {
			// Skip if already loading this exact conversation
			if (state.loadingConversation && state.singleConversation?.sid === sid) {
				return state.singleConversation;
			}

			const generation = state.selectionGeneration;
			const isCurrentContext = () => generation === state.selectionGeneration;

			try {
				commit("setLoadingConversation", true);

				const conversation = await ensureConversation(sid);

				if (!isCurrentContext()) return;

				// V1 only: the local list is the data source, so an empty list means
				// nothing selectable. V2 fetches by sid — a resolved conversation is
				// selectable even before the first list page has landed.
				if (!conversation?.sid ||
					(state.messagingVersion < 2 && !getters.hasConversation)) {
					if (state.messagingVersion < 2 && state.singleConversation?.sid === sid) {
						return state.singleConversation;
					}

					commit("resetSelectedConversation");
					return;
				}

				const isSameConversation = sid === state.singleConversation?.sid;

				// Clear previous conversation's state only if switching conversations
				if (!isSameConversation) {
					commit("resetSelectedConversation");
				}

				// Set the current conversation before loading messages
				commit("setSingleConversation", conversation);

				if (state.messagingVersion >= 2) {
					// V2: no initial REST fetch — the setSingleConversation commit above
					// (re)subscribes the conversation's realtime message listener, whose
					// initial snapshot delivers the first page (see conversation-v2.js).
					commit("setLoadingMessagesError", false);

					if (!isSameConversation) {
						commit("setLoadingMessages", true);
					}
				} else {
					// V1: Load messages via Twilio SDK
					const shouldLoadMessages =
						!isSameConversation ||
						state.loadingMessagesError ||
						state.messages.length === 0 ||
						conversation.lastMessageIndex !== state.messages[state.messages.length - 1]?.index;

					if (shouldLoadMessages) {
						await dispatch("loadMessages", { conversation, sid });
					}
				}

				if (!isCurrentContext()) return;

				if (!isSameConversation) {
					bus.$emit("conversationChanged", state.singleConversation?.sid);
				}

				return conversation;
			} catch (err) {
				console.error(`Exception occurred in displayConversation. ${err}`);
			} finally {
				if (isCurrentContext()) commit("setLoadingConversation", false);
			}

		},

		async fetchMoreMessages({ state, commit }) {
			if (state.messagingVersion >= 2) {
				// V2: Use REST API with cursor pagination
				if (state.messagePagination?.hasMore) {
					const currentConversation = state.singleConversation;
					const pagination = state.messagePagination;
					const isCurrentPage = () =>
						state.singleConversation?.sid === currentConversation?.sid &&
						state.messagePagination === pagination;

					try {
						const result = await fetchMessages(currentConversation, {
							limit: 15,
							startAfter: pagination.nextCursor
						});

						// Guard: Check if we're still viewing the same conversation after async call
						if (!isCurrentPage()) {
							return;
						}

						const messageIds = new Set(state.messages.map(message => message.id).filter(Boolean));
						const messageSids = new Set(state.messages.map(message => message.sid ?? message.state?.sid).filter(Boolean));
						const olderMessages = result.items.filter(message => {
							const messageSid = message.sid ?? message.state?.sid;

							if ((message.id && messageIds.has(message.id)) ||
								(messageSid && messageSids.has(messageSid))) return false;

							if (message.id) messageIds.add(message.id);
							if (messageSid) messageSids.add(messageSid);

							return true;
						});

						commit("setMessagePagination", { hasMore: result.hasMore, nextCursor: result.nextCursor });
						commit("setMessages", [...olderMessages, ...state.messages]);
					} catch (error) {
						if (isCurrentPage()) commit("setLoadingMessagesError", true);

						console.error("Error fetching previous messages:", error);
					}
				}
			} else {
				// V1: Use Twilio SDK pagination
				if (state.twilioMessagePage?.hasPrevPage) {
					const currentSid = state.singleConversation?.sid;

					try {
						const previousPage = await state.twilioMessagePage.prevPage();

						// Guard: Check if we're still viewing the same conversation after async call
						if (state.singleConversation?.sid !== currentSid) {
							return;
						}

						commit("setTwilioMessagePage", previousPage);
						commit("setMessages", [...previousPage.items.filter(msg => !msg.attributes?.isFlowMessage), ...state.messages]);
					} catch (error) {
						commit("setLoadingMessagesError", true);
						console.error("Error fetching previous messages:", error);
					}
				}
			}
		},

		async findByPhoneNumber({ state }, phoneNumber) {

			if (state.messagingVersion >= 2) {
				// V2: Query backend REST API (scoped to the user's group)
				try {
					const response = await axios.get(`conversations/v2/phone/${phoneNumber}`);

					return response.data?.sid;
				} catch (err) {
					// no error handling needed
				}
			} else {
				// V1: Search local state
				if (phoneNumber?.length) {
					let found = state.actives.find((sid) => getConversation(sid)?.friendlyName === phoneNumber);

					if (found) {
						return found;
					}

					return state.inactives.find((sid) => getConversation(sid)?.friendlyName === phoneNumber);
				}
			}
		},

		async getAllSurveys(context, id) {
			const { data: allSurveys } = await axios.get(`patients/${id}/surveys`);

			if (allSurveys.patientSurveys) {
				context.commit("setSurveys", allSurveys.patientSurveys);

				const unreadSurveyArray = allSurveys.patientSurveys.filter(survey => !survey.followupDone);

				if (unreadSurveyArray.length) context.state.hasUnreadSurveys = true;
			}
		},

		async getContactAppointments(context, id) {
			const { data: allAppointments } = await axios.get(`patients/${id}/appointments`);

			let oldAppointments = [];

			let pendingAppointments= [];

			allAppointments.map(appointment => {
				const appointmentDate = moment(appointment.start, "YYYY-MM-DD").format("YYYY-MM-DD");
				const currentDate = moment().format("YYYY-MM-DD");

				if (moment(appointmentDate).isBefore(currentDate)) oldAppointments.push(appointment);
				else pendingAppointments.push(appointment);
			});

			if (allAppointments.length) context.commit("setContactAppointments", allAppointments);
			if (oldAppointments.length) context.commit("setOldContactAppointments", oldAppointments);
			if (pendingAppointments.length) context.commit("setPendingContactAppointments", pendingAppointments);
		},

		async clearNotifications({ getters, state }, conversation) {

			if (state.messagingVersion >= 2) {
				await updateConvo(conversation.sid, { unread: false });
			} else {
				await axios.put(`conversations/sid/${conversation.sid}`, {
					attributes: {
						hasNotification: false,
						isAppointmentMicrosite: false,
						isInformationMicrosite: false
					}
				});
			}

			console.debug("Cleared notification for conversation:", getters.singleConversation?.sid);
		},

		async assignConversation({ state }, { conversation, userId }) {
			if (state.messagingVersion >= 2) {
				await updateConvo(conversation.sid, { userId });
			} else {
				await axios.put(`conversations/sid/${conversation.sid}`, {
					attributes: { assignedUserId: userId }
				});
			}
		},

		async pinConversation({ getters, state }, { conversation, pinned }) {
			// The caller has already persisted the flag; this action only applies
			// the optimistic V1 reorder. V2 pages are recommitted by the realtime
			// window.
			if (state.messagingVersion >= 2) return;

			let idx = state.actives.findIndex((sid) => sid === conversation.sid);

			if (idx > -1) {
				state.actives.splice(idx, 1);
			}

			const pinnedCount = state.actives.reduce((total, sid) => {
				const c = getConversation(sid);

				if (c?.pinned || c?.channelState?.attributes?.pinned) {
					return total + 1;
				}

				return total;
			}, 0);

			insertSorted(state.actives, conversation, pinned, pinnedCount);

			console.debug("Pin/unpin conversation:", getters.singleConversation?.sid, pinned);
		},

		async toggleNotified({ state }, { conversation, value }) {
			if (state.messagingVersion >= 2) {
				await updateConvo(conversation.sid, { unread: value });
			} else {
				await axios.put(`conversations/sid/${conversation.sid}`, {
					attributes: { hasNotification: value }
				});
			}

			console.debug(
				`Marked the conversation as ${value ? "unread" : "read"}:`,
				conversation.sid
			);
		},

		async createConversationPayload({ rootGetters }, conversationPayload) {
			const { to, message } = conversationPayload;

			return {
				// When no from and identity sent. The main conversation group is used
				// from: twilioInfo.phoneNumber,
				// identity: twilioInfo.identity,
				to: to.replace(/\s/g, ""),
				clinicId: rootGetters["login/profile"].clinic,
				...message && {
					body: message
				}
			};
		},

		async markAppointmentMicrositeConfirmed({ state }) {
			// V2 has no conversation-level microsite flag — nothing to persist.
			if (state.messagingVersion >= 2) return;

			if (state.selectedAppointmentMessage?.sid) {
				const conversationSid =
					state.selectedAppointmentMessage.conversationSid ??
					state.selectedAppointmentMessage.conversation?.sid;

				if (!conversationSid) return;

				await axios.put(`conversations/sid/${conversationSid}`, {
					attributes: { isConfirmed: true }
				});
			}
		},

		async addConversation({ state, dispatch }, conversation) {
			if (!conversation?.sid) {
				console.error("addConversation. No conversation SID found");
				return;
			}

			try {
				await mutex.lock();

				if (state.singleConversation?.sid === conversation.sid) {
					dispatch("selectConversation", conversation.sid);
				}

				if (state.addedConversations[conversation.sid]) {
					return;
				}

				console.debug("addConversation ", conversation);

				if (conversation.state.current === "active") {
					insertSorted(state.actives, conversation);
					dispatch("updateActiveNotification", { conversation });
				} else {
					insertSorted(state.inactives, conversation);
					dispatch("updateInactiveNotification", { conversation });
				}

				state.addedConversations[conversation.sid] = true;
			} catch (error) {
				console.error("Error adding conversation:", error);
			} finally {
				mutex.unlock();
				bus.$emit("conversation-updated", conversation.sid);
			}
		},

		async updateConversation({ state, commit, dispatch }, { updateReasons, conversation }) {
			if (!conversation?.sid) {
				console.error("updateConversation. No conversation SID found");
				return;
			}

			try {
				await mutex.lock();

				if (!state.addedConversations[conversation.sid]) {
					dispatch("addConversation", conversation);
					return;
				}

				console.debug("updateConversation ", conversation.sid, " reasons ", updateReasons);

				const status = conversation.state.current;
				const statusUpdated = updateReasons.includes("state");

				if (updateReasons.includes("attributes") || updateReasons.includes("unread") || statusUpdated) {
					if (status === "active") {
						dispatch("updateActiveNotification", { conversation });
					} else {
						dispatch("updateInactiveNotification", { conversation });
					}
				}

				if (statusUpdated) {
					// Prevent duplicate processing when the status is already the same.
					const isInActives = state.actives.includes(conversation.sid);

					if (status === "active" && !isInActives) {
						let idx = state.inactives.findIndex((sid) => sid === conversation.sid);

						if (idx > -1) {
							state.inactives.splice(idx, 1);
						}

						insertSorted(state.actives, conversation);
					} else if (status !== "active" && isInActives) {
						let idx = state.actives.findIndex((sid) => sid === conversation.sid);

						if (idx > -1) {
							state.actives.splice(idx, 1);
						}

						insertSorted(state.inactives, conversation);
					}
				} else if (updateReasons.includes("dateUpdated") ||
					updateReasons.includes("pinned") ||
					updateReasons.includes("attributes")
				) {
					// V1 stores pinned in attributes, so attribute updates can change
					// the ordering as well as date updates.
					const type = conversation.state.current === "active" ? "active" : "inactive";
					const array = type === "active" ? state.actives : state.inactives;

					// Check the conversation against its current neighbors: a binary
					// search is unreliable here because the conversation still sits at
					// its stale position, making the array locally unsorted.
					const idx = array.indexOf(conversation.sid);
					const prev = idx > 0 ? getConversation(array[idx - 1]) : null;
					const next = idx > -1 && idx < array.length - 1 ? getConversation(array[idx + 1]) : null;

					const misplaced = idx === -1 ||
						(prev && compareConversations(conversation, prev) < 0) ||
						(next && compareConversations(next, conversation) < 0);

					if (misplaced) {
						commit("sort", type);
					}
				}
			} finally {
				mutex.unlock();
				bus.$emit("conversation-updated", conversation.sid);
			}
		},

		async updateConversationStateIfNoEvent({ state, dispatch }, { sid, newState, timeout }) {
			if (state.messagingVersion >= 2) return;

			if (!sid) {
				console.error("updateConversationStateIfNoEvent: No conversation SID provided");
				return;
			}

			console.debug(`Waiting briefly for possible event update of conversation ${sid}...`);

			// Wait to give time for a possible event to arrive
			await new Promise((resolve) => setTimeout(resolve, timeout));

			// Check if status has already been updated during the wait
			const targetList = newState === "active" ? state.actives : state.inactives;
			const isStatusUpdated = targetList.includes(sid);

			// Status already updated. Nothing needs to be done.
			if (isStatusUpdated) {
				console.debug(`Conversation ${sid} was updated via event — skipping manual update.`);
				return;
			}

			// No event received — force update from Twilio
			const conversation = getConversation(sid);

			if (!conversation) {
				console.warn(`Conversation ${sid} not found locally — cannot force update.`);
				return;
			}

			// New state doesn't match the actual state from Twilio — local data is outdated, no action needed.
			if (newState !== conversation.state.current) {
				return;
			}

			// Re-check before forcing update — event may have arrived after timeout
			const newTargetList = newState === "active" ? state.actives : state.inactives;
			const stillUpdated = newTargetList.includes(sid);

			if (stillUpdated) {
				console.debug(`Conversation ${sid} was updated right before manual dispatch — skipping update.`);
				return;
			}

			await dispatch("updateConversation", {
				updateReasons: ["state"],
				conversation
			});
		},

		async removeConversation({ state, commit }, conversation) {
			const conversationSid = conversation.sid;

			if (state.singleConversation?.sid === conversationSid &&
				(conversation.participants?.size ?? 0) < 2) {
				// If currently selected and unlinked, do not remove it
				return;
			}

			try {
				await mutex.lock();

				console.debug("removeConversation ", conversationSid);

				commit("removeFromNotifications", conversationSid);

				if (state.singleConversation?.sid === conversationSid) {
					commit("resetSelectedConversation");
				}

				if (state.addedConversations[conversationSid]) {
					delete state.addedConversations[conversationSid];
				}

				if (conversation.state.current === "active") {
					let idx = state.actives.findIndex((sid) => sid === conversationSid);

					if (idx > -1) {
						state.actives.splice(idx, 1);
					}
				} else {
					let idx = state.inactives.findIndex((sid) => sid === conversationSid);

					if (idx > -1) {
						state.inactives.splice(idx, 1);
					}
				}
			} finally {
				mutex.unlock();
			}
		},

		updateActiveNotification({ state, commit }, { conversation }) {
			const sid = conversation?.sid;

			if (!sid) return;

			const hasNotification = conversation.unread ||
					conversation.attributes?.hasNotification ||
					conversation.channelState?.attributes?.hasNotification;

			if (hasNotification) {
				if (state.hasNotificationInactiveSids.has(sid)) {
					state.hasNotificationInactiveSids.delete(sid);
					state.unreadInactiveConversationCount = state.hasNotificationInactiveSids.size;
				}

				if (!state.hasNotificationSids.has(sid)) {
					state.hasNotificationSids.add(sid);
					state.unreadConversationCount = state.hasNotificationSids.size;
				}
			} else {
				commit("removeFromNotifications", sid);
			}
		},

		updateInactiveNotification({ state, commit }, { conversation }) {
			const sid = conversation?.sid;

			if (!sid) return;

			const hasNotification = conversation.unread ||
					conversation.attributes?.hasNotification ||
					conversation.channelState?.attributes?.hasNotification;

			if (hasNotification) {
				if (state.hasNotificationSids.has(sid)) {
					state.hasNotificationSids.delete(sid);
					state.unreadConversationCount = state.hasNotificationSids.size;
				}

				if (!state.hasNotificationInactiveSids.has(sid)) {
					state.hasNotificationInactiveSids.add(sid);
					state.unreadInactiveConversationCount = state.hasNotificationInactiveSids.size;
				}
			} else {
				commit("removeFromNotifications", sid);
			}
		}
	},

	namespaced: true,
	state: () => ({
		userGroupId: null,
		actives: [],
		inactives: [],
		addedConversations: {},
		pinned: [],

		hasNotificationInactiveSids: new Set(),
		hasNotificationSids: new Set(),
		unreadConversationCount: 0,
		unreadInactiveConversationCount: 0,
		hasNextActive: false,
		hasPrevActive: false,
		hasNextInactive: false,
		hasPrevInactive: false,
		loaded: false,

		loadingAllConversations: false,
		loadingConversation: false,
		loadingMessages: false,
		loadingMessagesError: false,
		loadingPatient: false,

		selectionGeneration: 0,
		singleConversation: null,
		patient: null,
		messages: [],
		parentDialog: false,
		surveyArray: [],
		followupCheckbox: false,
		twilioMessagePage: undefined,
		messagePagination: { hasMore: false, nextCursor: null },
		appointmentUI: false,
		appointmentIdModify: undefined,
		appointmentRequest: false,
		newMessage : false,
		hasUnreadSurveys : false,
		audioNotificationsEnabled : false,
		isInitializing: true,
		connectionState: "disconnected", /* DISCONNECTED */
		appointmentUpdate: false,
		selectedContactsTab: 0,
		isAttachmentSent: false,
		openContactsDrawer: false,
		sendContentOpen: false,
		desktopNotificationsEnabled: false,
		browserNotificationsDisabled: false,
		showNotificationsAlert: ALERT_HIDDEN,
		toggleStatusLoading: false,
		signatureOption: SIGNATURE_OPTION.NO_SIGNATURE,
		isContactsSearch: false,
		awaitingConversationCreation: false,
		focusSendMessageInput: false,
		contactAppointments: [],
		contactOldAppointments: [],
		contactPendingAppointments: [],
		activeSendBoxTab: TEXTO_TAB,
		prepareToCallLoader: false,
		messagingVersion: 1
	})
};

export default conversationModule;
